// src/oura/components/plan/planModel.ts
//
// The staff plan card's rules, pure (plan table spec v1.1, 29 Sep 2026): how
// a months field is read, which time group and sub-group a line sits in, what
// a group head says about this month, what counts as a change, and how a
// refusal from the server maps back onto the lines that were sent.
//
// Grouping is display only. The record keeps its own order, and a save sends
// the lines in that order with new ones appended. Nothing here is ever
// dropped: every line lands in exactly one time group, and a line the rules
// do not know (a custom X code, a catalogue line moved out of its months)
// lands in its time group's "Other".
//
// No runtime imports (types only), so it can be exercised on its own.

import type { Deliverable, DeliverableSeed, Engagement, EngagementTracking, OutletNameSent } from "@service/aeo/clientReview";
import type { Family } from "../../ui/tokens";

/* ------------------------------------------------------------------------- *
 * Words, codes and checks
 * ------------------------------------------------------------------------- */

/**
 * How a line is proven: the site scan reads its checks, or the team confirms
 * it. Named for the instrument, not for Oura: A1, G3, G6 and G7 are Oura's own
 * runs, yet no site-scan check covers them, so "Oura checks it" on the other
 * mark would have undercounted what Oura does.
 */
export const COVERED_WORDS = "Site scan checks it";
export const BY_HAND_WORDS = "We confirm it";

/** "3 checks", "1 check". */
export function checksText(n: number): string {
    return `${n} ${n === 1 ? "check" : "checks"}`;
}

/** "Oura checks it · 3 checks" or "We confirm it". */
export function coversWords(line: { covers: string[]; reported_by_hand: boolean }): string {
    return line.covers.length > 0 && !line.reported_by_hand ? `${COVERED_WORDS} · ${checksText(line.covers.length)}` : BY_HAND_WORDS;
}

/**
 * `aeo_catalogue.COVERS` as the server holds it (ourteam-backend#4814), for
 * SHOWING a line seeded before #4814 only: such a row carries no `covers` and
 * no `reported_by_hand`, and the server authors both from this map when it
 * reads or saves the row. The editor never sends it: a line that came without
 * the fields goes back without them, so the server's own map stays the one
 * that counts.
 */
export const CATALOGUE_COVERS: Readonly<Record<string, readonly string[]>> = {
    S3: ["broken_4xx", "broken_outbound", "redirect_chains"],
    S1: ["missing_meta", "dup_titles", "missing_alt"],
    S6: ["orphan_pages"],
    S7: ["cwv"],
    A2: ["no_schema"],
    A3: ["robots_missing"],
    G2: ["broken_4xx", "redirect_chains", "canonicals", "thin_content"],
};

/** The catalogue family a code's chip is coloured by; anything else is custom work (brown). */
export function familyOf(code: string): Family {
    const f = code.trim().charAt(0).toLowerCase();
    return f === "s" || f === "a" || f === "b" || f === "g" ? f : "x";
}

/** One check as the Site tab last read it: its label and how many pages fail it. */
export interface PlanCheck {
    label: string;
    count: number;
}
/** Keyed by check key. A key that is absent has not been measured for this business. */
export type PlanChecks = Record<string, PlanCheck>;

/**
 * A site audit's checks by key, as the plan's drawer reads them. Undefined
 * with no audit, so every covered check reads "not measured yet", never clear.
 */
export function contractChecks(audit: { issues: Array<{ key: string; label: string; count: number }> } | null | undefined): PlanChecks | undefined {
    if (!audit) return undefined;
    return Object.fromEntries(audit.issues.map((i) => [i.key, { label: i.label, count: i.count }]));
}

/* ------------------------------------------------------------------------- *
 * Months
 * ------------------------------------------------------------------------- */

/** The three shapes a months field takes, as the help line and the errors say them. */
export const MONTHS_EXAMPLE = "1, 2-12 or 3, 6, 9";

export type MonthsParse = { ok: true; months: number[] } | { ok: false; error: string };

/**
 * "1", "2-12", "3, 6, 9" read strictly. Nothing is clamped or dropped: a
 * month past the term, a reversed range and a stray word are each an error,
 * never a shorter list. Empty parts ("2, 3,") are skipped.
 */
export function parseMonths(text: string, term: number): MonthsParse {
    const out = new Set<number>();
    let bad = false;
    let reversed = false;
    let zero = false;
    let past: number | null = null;
    const take = (n: number) => {
        if (n === 0) zero = true;
        else if (n > term) past = past === null ? n : Math.min(past, n);
        else out.add(n);
    };
    let parts = 0;
    for (const part of text.split(",")) {
        const p = part.trim();
        if (!p) continue;
        parts += 1;
        const range = p.match(/^(\d+)\s*[-–]\s*(\d+)$/);
        if (range) {
            const a = Number(range[1]);
            const b = Number(range[2]);
            if (a > b) {
                reversed = true;
                continue;
            }
            if (a === 0) zero = true;
            // Never walk past the term: "1-1000000" is one error, not a long loop.
            for (let i = Math.max(a, 1); i <= Math.min(b, term); i++) out.add(i);
            if (b > term) take(Math.max(a, term + 1));
        } else if (/^\d+$/.test(p)) {
            take(Number(p));
        } else {
            bad = true;
        }
    }
    if (bad) return { ok: false, error: `Use month numbers, like ${MONTHS_EXAMPLE}.` };
    if (reversed) return { ok: false, error: "Write the range low to high, like 2-12." };
    if (zero) return { ok: false, error: "Months start at 1." };
    if (past !== null) return { ok: false, error: `Month ${past} is past the end of the ${term}-month plan.` };
    if (parts === 0 || out.size === 0) return { ok: false, error: "Pick at least one month." };
    return { ok: true, months: Array.from(out).sort((a, b) => a - b) };
}

/** [2,3,4,5] -> "2-5"; [6,12] -> "6, 12"; [] -> "". */
export function formatMonths(months: number[]): string {
    const sorted = normMonths(months);
    if (sorted.length === 0) return "";
    const runs: string[] = [];
    let start = sorted[0];
    let prev = sorted[0];
    const push = () => runs.push(start === prev ? `${start}` : `${start}-${prev}`);
    for (const m of sorted.slice(1)) {
        if (m === prev + 1) {
            prev = m;
            continue;
        }
        push();
        start = m;
        prev = m;
    }
    push();
    return runs.join(", ");
}

/** "Month 4" or "Months 2-5", "Months 6, 12". */
export function monthsPhrase(months: number[]): string {
    const sorted = normMonths(months);
    return sorted.length === 1 ? `Month ${sorted[0]}` : `Months ${formatMonths(sorted)}`;
}

function normMonths(months: readonly number[]): number[] {
    return Array.from(new Set(months.filter((m) => Number.isInteger(m)))).sort((a, b) => a - b);
}

function sameMonths(a: readonly number[], b: readonly number[]): boolean {
    const x = normMonths(a);
    const y = normMonths(b);
    return x.length === y.length && x.every((m, i) => m === y[i]);
}

/* ------------------------------------------------------------------------- *
 * Groups
 * ------------------------------------------------------------------------- */

export type TimeGroup = "none" | "foundation" | "monthly" | "milestones";
export type SubGroup = "measure" | "fix" | "write" | "google" | "handover" | "main" | "other";

/** When a set of months happens. Sorted, unique months in. */
export function timeOf(months: readonly number[]): TimeGroup {
    const m = normMonths(months);
    if (m.length === 0) return "none";
    if (m.length === 1 && m[0] === 1) return "foundation";
    if (m.length >= 2 && m[m.length - 1] - m[0] === m.length - 1) return "monthly";
    return "milestones";
}

/** Where each catalogue code belongs when it runs as sold. */
const HOME: Record<string, TimeGroup> = {
    A1: "foundation", A2: "foundation", A3: "foundation", A4: "foundation", B1: "foundation",
    S1: "foundation", S2: "foundation", S3: "foundation", S4: "foundation", S5: "foundation", S6: "foundation", S7: "foundation",
    G1: "monthly", G2: "monthly", G3: "monthly", G4: "monthly", G5: "monthly", G6: "monthly", G7: "monthly",
    G8: "milestones",
};

/** Month 1's sub-groups, in the order they read. */
const FOUNDATION_SUB: Record<string, SubGroup> = {
    A1: "measure", S5: "measure",
    S3: "fix", S1: "fix", S6: "fix", S7: "fix", A2: "fix", A3: "fix",
    B1: "write",
    S2: "google", S4: "google",
    A4: "handover",
};

export const SUB_ORDER: SubGroup[] = ["measure", "fix", "write", "google", "handover", "main", "other"];

export const SUB_TITLE: Record<SubGroup, string> = {
    measure: "Measure",
    fix: "Fix the site",
    write: "Write",
    google: "Google and listings",
    handover: "Hand over",
    main: "",
    other: "Other",
};

export const GROUP_ORDER: TimeGroup[] = ["none", "foundation", "monthly", "milestones"];

export function groupId(t: TimeGroup): string {
    return `plan.${t}`;
}

/** The time group and sub-group a line sits in. */
export function place(code: string, months: readonly number[]): { time: TimeGroup; sub: SubGroup } {
    const time = timeOf(months);
    const c = code.trim().toUpperCase();
    if (HOME[c] === time) return { time, sub: time === "foundation" ? FOUNDATION_SUB[c] ?? "other" : "main" };
    return { time, sub: "other" };
}

/** The number after a code's letter: "G12" -> 12. Unknown shapes sort last. */
export function codeNumber(code: string): number {
    const m = code.trim().match(/(\d+)/);
    return m ? Number(m[1]) : Number.MAX_SAFE_INTEGER;
}

function byCode(a: string, b: string): number {
    const x = a.trim().toUpperCase();
    const y = b.trim().toUpperCase();
    if (x.charAt(0) !== y.charAt(0)) return x.localeCompare(y);
    return codeNumber(x) - codeNumber(y) || x.localeCompare(y);
}

/**
 * The monthly group's title, from its commonest run (ties to the earliest
 * first month): "Every month, from month 2" when the run reaches the end of
 * the term, else "Every month, months 3 to 8".
 */
export function monthlyTitle(runs: Array<readonly number[]>, term: number): string {
    const best = commonestRun(runs);
    if (!best) return "Every month";
    return best.last >= term ? `Every month, from month ${best.first}` : `Every month, months ${best.first} to ${best.last}`;
}

/** The run most rows share, ties to the earliest first month. */
export function commonestRun(runs: Array<readonly number[]>): { first: number; last: number } | null {
    const tally = new Map<string, { first: number; last: number; n: number }>();
    for (const r of runs) {
        const m = normMonths(r);
        if (m.length === 0) continue;
        const first = m[0];
        const last = m[m.length - 1];
        const key = `${first}-${last}`;
        const t = tally.get(key) ?? { first, last, n: 0 };
        t.n += 1;
        tally.set(key, t);
    }
    const best = Array.from(tally.values()).sort((a, b) => b.n - a.n || a.first - b.first || a.last - b.last)[0];
    return best ? { first: best.first, last: best.last } : null;
}

/* ------------------------------------------------------------------------- *
 * The draft
 * ------------------------------------------------------------------------- */

/** One line as the editor holds it: the saved deliverable (null for a new line) and the fields as typed. */
export interface DraftLine {
    rid: string;
    saved: Deliverable | null;
    code: string;
    label: string;
    monthsText: string;
    quantityText: string;
    /** As the record has it, or (a row from before #4814) as the server's catalogue authors it. */
    covers: string[];
    reported_by_hand: boolean;
    /**
     * The record carries `covers` / `reported_by_hand`. False on a row seeded
     * before #4814: the field is left out of the save, so the server authors it
     * from its catalogue as it does on read. Sending `[]` would switch that
     * authoring off and turn the line into "We confirm it" for good.
     */
    coversOnRecord: boolean;
    handOnRecord: boolean;
    cadence: string | null;
    note: string | null;
}

export function lineFromDeliverable(d: Deliverable, rid: string): DraftLine {
    // Read loosely: a legacy row has neither field, whatever the type says.
    const raw = d as Partial<Pick<Deliverable, "covers" | "reported_by_hand">>;
    const coversOnRecord = Array.isArray(raw.covers);
    const handOnRecord = typeof raw.reported_by_hand === "boolean";
    const covers = coversOnRecord ? (raw.covers as string[]) : [...(CATALOGUE_COVERS[d.code.trim().toUpperCase()] ?? [])];
    return {
        rid,
        saved: d,
        code: d.code,
        label: d.label,
        monthsText: formatMonths(d.months ?? []),
        quantityText: d.quantity ?? "",
        covers,
        reported_by_hand: handOnRecord ? (raw.reported_by_hand as boolean) : covers.length === 0,
        coversOnRecord,
        handOnRecord,
        cadence: d.cadence,
        note: d.note,
    };
}

/** Oura checks it: the line carries scan checks and is not reported by hand. */
export function isCovered(line: Pick<DraftLine, "covers" | "reported_by_hand">): boolean {
    return line.covers.length > 0 && !line.reported_by_hand;
}

export function labelError(line: Pick<DraftLine, "label">): string | null {
    return line.label.trim() ? null : "Say what the client sees.";
}

export function monthsError(line: Pick<DraftLine, "monthsText">, term: number): string | null {
    const p = parseMonths(line.monthsText, term);
    return p.ok ? null : p.error;
}

function quantityOf(text: string): string | null {
    return text.trim() || null;
}

/** A saved line whose fields, read as they would be sent, differ from what is saved. A new line is not "edited". */
export function isEdited(line: DraftLine, term: number): boolean {
    const s = line.saved;
    if (!s) return false;
    if (line.label.trim() !== (s.label ?? "").trim()) return true;
    if (quantityOf(line.quantityText) !== quantityOf(s.quantity ?? "")) return true;
    if (line.monthsText.trim() === formatMonths(s.months ?? [])) return false;
    const p = parseMonths(line.monthsText, term);
    return !p.ok || !sameMonths(p.months, s.months ?? []);
}

/**
 * A line that cannot be sent as it stands. An untouched saved line always
 * can: it goes back as it was read, so a legacy line with no months never
 * blocks a save nobody made on it.
 */
export function isInvalid(line: DraftLine, term: number): boolean {
    if (line.saved && !isEdited(line, term)) return false;
    return Boolean(labelError(line) || monthsError(line, term));
}

/** The months a line is placed by: as typed when they read, else as saved, else none. */
export function placementMonths(line: DraftLine, term: number): number[] {
    const p = parseMonths(line.monthsText, term);
    if (p.ok) return p.months;
    return normMonths(line.saved?.months ?? []);
}

/** The months a line is sent with: as typed when they read, else as saved (a legacy empty list). */
function sentMonths(line: DraftLine, term: number): number[] {
    const p = parseMonths(line.monthsText, term);
    return p.ok ? p.months : normMonths(line.saved?.months ?? []);
}

/**
 * The line as it is sent. `code`, `covers`, `reported_by_hand`, `cadence`
 * and `note` go back exactly as read, and a field the record did not carry
 * is left out, never filled in (a row from before #4814); the label is
 * trimmed, the quantity trimmed or null, the months strictly parsed. An
 * untouched saved line with months the parser refuses (a legacy empty list)
 * goes back with its own.
 */
export function toDeliverable(line: DraftLine, term: number): DeliverableSeed {
    const out: DeliverableSeed = {
        code: line.code,
        label: line.label.trim(),
        months: sentMonths(line, term),
        quantity: quantityOf(line.quantityText),
        cadence: line.cadence,
        note: line.note,
    };
    if (line.coversOnRecord) out.covers = line.covers;
    if (line.handOnRecord) out.reported_by_hand = line.reported_by_hand;
    return out;
}

/* ------------------------------------------------------------------------- *
 * Pages: the server's own rule
 * ------------------------------------------------------------------------- */

/** The leading number in "3 a month", as the server reads it (`_monthly_units`); null when there is none. */
export function monthlyUnits(quantity: string | null | undefined): number | null {
    const m = (quantity ?? "").match(/^\s*(\d+)/);
    return m ? Number(m[1]) : null;
}

/** The codes whose monthly quantity is the contract's page budget (`PAGE_UNIT_CODES` on the server). */
export const PAGE_UNIT_CODES: readonly string[] = ["G1"];

export interface PageGap {
    /** Pages the lines deliver, as the server counts them. */
    implied: number;
    /** Pages the contract sells. */
    sold: number;
    /** The line that carries them (G1), when there is one. */
    rid: string | null;
    perMonth: number | null;
    months: number;
}

/**
 * The page sum the server refuses a save on, or null when it adds up. The
 * server counts G1 alone: its quantity's leading number times its months,
 * against the contract's page units (`_validate_deliverables`), and any save
 * that sends the lines is checked, whichever line changed. No page units on
 * the contract: nothing is checked.
 */
export function pageGap(lines: DraftLine[], term: number, sold: number | null | undefined): PageGap | null {
    if (typeof sold !== "number") return null;
    let implied = 0;
    let rid: string | null = null;
    let perMonth: number | null = null;
    let months = 0;
    for (const l of lines) {
        if (!PAGE_UNIT_CODES.includes(l.code.trim().toUpperCase())) continue;
        const per = monthlyUnits(l.quantityText);
        const m = sentMonths(l, term).length;
        if (rid === null) {
            rid = l.rid;
            perMonth = per;
            months = m;
        }
        if (per !== null) implied += per * m;
    }
    return implied === sold ? null : { implied, sold, rid, perMonth, months };
}

/** Each added, removed or edited line counts 1, and a moved start date counts 1. */
export function changeCount(lines: DraftLine[], removed: number, term: number, startChanged: boolean): number {
    let n = removed + (startChanged ? 1 : 0);
    for (const l of lines) if (!l.saved || isEdited(l, term)) n += 1;
    return n;
}

/** X1 -> 1; anything else -> null. */
export function xNumber(code: string): number | null {
    const m = code.trim().match(/^X(\d+)$/i);
    return m ? Number(m[1]) : null;
}

/** The highest custom number in a set of codes, 0 when there is none. */
export function xHighWater(codes: string[]): number {
    return codes.reduce((hi, c) => Math.max(hi, xNumber(c) ?? 0), 0);
}

/** The next custom code after the highest one this session has seen: X1, X2, never a second X2. */
export function nextXCode(highWater: number): string {
    return `X${highWater + 1}`;
}

/**
 * The plan has not begun: no start date, or one after today. `monthOf` alone
 * would say month 1 for both. Dates as YYYY-MM-DD.
 */
export function isNotStarted(start: string | null | undefined, todayIso: string): boolean {
    const s = (start ?? "").slice(0, 10);
    return !s || s > todayIso;
}

/** Oura checks it vs We confirm it, over the lines as they stand. */
export function coverCounts(lines: Array<Pick<DraftLine, "covers" | "reported_by_hand">>): { checked: number; confirmed: number } {
    let checked = 0;
    for (const l of lines) if (isCovered(l)) checked += 1;
    return { checked, confirmed: lines.length - checked };
}

/* ------------------------------------------------------------------------- *
 * The view: groups, sub-groups and where this month falls
 * ------------------------------------------------------------------------- */

export type GroupState = "current" | "past" | "future" | "not_started" | "none";

export interface ViewRow {
    line: DraftLine;
    /** The months it is placed by. */
    months: number[];
    sub: SubGroup;
    isNew: boolean;
    edited: boolean;
    labelErr: string | null;
    monthsErr: string | null;
    invalid: boolean;
    /** It runs in this month of the term (never before the plan starts). */
    runsNow: boolean;
}

export interface ViewSub {
    key: SubGroup;
    /** Null: no sub-head. */
    title: string | null;
    rows: ViewRow[];
}

export interface ViewGroup {
    key: TimeGroup;
    id: string;
    title: string;
    subs: ViewSub[];
    /** Every row, in on-screen order across the sub-groups. */
    rows: ViewRow[];
    state: GroupState;
    /** Past: the last month any row runs. */
    endMonth: number | null;
    /** Future or not started: the next month a row runs. */
    nextMonth: number | null;
    /** The months a row must match to leave out its own months: [1] for Foundation, the title's run for the monthly group. */
    headMonths: number[] | null;
}

/**
 * The card's groups, in reading order, with every draft line in exactly one.
 * `month` is the term month (monthOf); `notStarted` is no start date, or one
 * after today.
 */
export function buildPlanView(lines: DraftLine[], opts: { term: number; month: number; notStarted: boolean }): ViewGroup[] {
    const { term, month, notStarted } = opts;
    const byTime = new Map<TimeGroup, ViewRow[]>();
    const order = new Map<string, number>(lines.map((l, i) => [l.rid, i]));
    for (const line of lines) {
        const months = placementMonths(line, term);
        const { time, sub } = place(line.code, months);
        const labelErr = labelError(line);
        const monthsErr = monthsError(line, term);
        const row: ViewRow = {
            line,
            months,
            sub,
            isNew: !line.saved,
            edited: isEdited(line, term),
            labelErr,
            monthsErr,
            invalid: isInvalid(line, term),
            runsNow: !notStarted && months.includes(month),
        };
        const list = byTime.get(time) ?? [];
        list.push(row);
        byTime.set(time, list);
    }

    const groups: ViewGroup[] = [];
    for (const key of GROUP_ORDER) {
        const rows = byTime.get(key);
        if (!rows || rows.length === 0) continue;

        const bySub = new Map<SubGroup, ViewRow[]>();
        for (const r of rows) bySub.set(r.sub, [...(bySub.get(r.sub) ?? []), r]);
        const recordOrder = (a: ViewRow, b: ViewRow) => (order.get(a.line.rid) ?? 0) - (order.get(b.line.rid) ?? 0);
        const subs: ViewSub[] = [];
        for (const s of SUB_ORDER) {
            const list = bySub.get(s);
            if (!list || list.length === 0) continue;
            if (s === "other") {
                list.sort((a, b) => (a.months[0] ?? 0) - (b.months[0] ?? 0) || byCode(a.line.code, b.line.code) || recordOrder(a, b));
            } else if (s === "main") {
                list.sort((a, b) => byCode(a.line.code, b.line.code) || recordOrder(a, b));
            } else {
                // Foundation: the sub-groups read in their own order, and inside
                // each the lines keep the record's order (spec 3, row order).
                list.sort(recordOrder);
            }
            const above = subs.reduce((n, x) => n + x.rows.length, 0);
            const title = key === "foundation" && s !== "other" ? SUB_TITLE[s] : s === "other" && above > 0 ? SUB_TITLE.other : null;
            subs.push({ key: s, title, rows: list });
        }
        const flat = subs.flatMap((s) => s.rows);

        let title: string;
        let headMonths: number[] | null = null;
        if (key === "none") title = "No month set";
        else if (key === "foundation") {
            title = "Month 1 · Foundation";
            headMonths = [1];
        } else if (key === "monthly") {
            // The head names the lines that run as sold; a moved or custom
            // line in "Other" never sets it.
            const main = subs.find((x) => x.key === "main")?.rows ?? [];
            const runs = (main.length > 0 ? main : flat).map((r) => r.months);
            title = monthlyTitle(runs, term);
            const run = commonestRun(runs);
            if (run) headMonths = Array.from({ length: run.last - run.first + 1 }, (_, i) => run.first + i);
        } else title = "Milestones";

        // Where this month falls for the group.
        let state: GroupState;
        let endMonth: number | null = null;
        let nextMonth: number | null = null;
        const all = flat.flatMap((r) => r.months);
        if (key === "none") state = "none";
        else if (notStarted) {
            state = "not_started";
            nextMonth = smallestAbove(all, 1);
        } else if (flat.some((r) => r.months.includes(month))) state = "current";
        else if (flat.every((r) => r.months.length > 0 && r.months[r.months.length - 1] < month)) {
            state = "past";
            endMonth = Math.max(...all);
        } else {
            state = "future";
            nextMonth = smallestAbove(all, month);
        }

        groups.push({ key, id: groupId(key), title, subs, rows: flat, state, endMonth, nextMonth, headMonths });
    }
    return groups;
}

/**
 * What a row says in the late slot, in this order: a field to fix (only on a
 * line someone changed), a line the server refused, a line with no month,
 * the line whose pages do not add up to the contract's.
 */
export function rowLate(row: Pick<ViewRow, "invalid" | "labelErr" | "months">, refused: boolean, pagesOff = false): string | null {
    if (row.invalid) return row.labelErr ? "Label to fix" : "Months to fix";
    if (refused) return "Refused on save";
    if (row.months.length === 0) return "No month";
    if (pagesOff) return "Pages to fix";
    return null;
}

/** A group's unsaved and to-fix lines, for the note on a folded head. */
export function groupCounts(g: Pick<ViewGroup, "rows">, refused: ReadonlySet<string>): { unsaved: number; toFix: number } {
    let unsaved = 0;
    let toFix = 0;
    for (const r of g.rows) {
        if (r.isNew || r.edited) unsaved += 1;
        if (r.invalid || refused.has(r.line.rid)) toFix += 1;
    }
    return { unsaved, toFix };
}

function smallestAbove(months: number[], m: number): number | null {
    const above = months.filter((x) => x > m);
    return above.length ? Math.min(...above) : null;
}

/** A group starts open when it is this month's, when it holds lines with no month, or (before the start) when it is Foundation. */
export function groupStartsOpen(g: Pick<ViewGroup, "key" | "state">): boolean {
    if (g.state === "current" || g.state === "none") return true;
    if (g.state === "not_started") return g.key === "foundation";
    return false;
}

/** The months a row shows beside its quantity: only where they differ from its group head. */
export function rowMonthsFact(row: Pick<ViewRow, "months">, group: Pick<ViewGroup, "key" | "headMonths">): string | null {
    if (row.months.length === 0) return null;
    if (group.key === "milestones") return monthsPhrase(row.months);
    if (group.headMonths && sameMonths(row.months, group.headMonths)) return null;
    return monthsPhrase(row.months);
}

/** A quantity for a row: trimmed, cut at 32 characters with an ellipsis. */
export function shortQuantity(text: string, max = 32): string | null {
    const q = text.trim();
    if (!q) return null;
    return q.length > max ? `${q.slice(0, max - 1).trimEnd()}…` : q;
}

/**
 * The quantity a row shows, or null when the label already says it: "1 small
 * change" with a quantity of "1" reads the number twice. Each fact once; the
 * drawer still shows the field as it is.
 */
export function rowQuantity(label: string, quantity: string): string | null {
    const q = quantity.trim();
    if (!q) return null;
    const l = label.trim().toLowerCase();
    const ql = q.toLowerCase();
    return l === ql || l.startsWith(`${ql} `) ? null : q;
}

/** Where one line sits in time, for its drawer's clock and its greyed state. */
export type RowTiming =
    | { kind: "now" }
    | { kind: "ended"; month: number }
    | { kind: "next"; month: number }
    | { kind: "not_started" }
    | { kind: "none" };

export function rowTiming(months: number[], month: number, notStarted: boolean): RowTiming {
    if (months.length === 0) return { kind: "none" };
    if (notStarted) return { kind: "not_started" };
    if (months.includes(month)) return { kind: "now" };
    const next = smallestAbove(months, month);
    if (next !== null) return { kind: "next", month: next };
    return { kind: "ended", month: Math.max(...months) };
}

/* ------------------------------------------------------------------------- *
 * A refused save
 * ------------------------------------------------------------------------- */

export interface Refusal {
    /** Messages per line, the "deliverables[i].field: " prefix removed. */
    byRid: Record<string, string[]>;
    /** Everything that is not a line's (pageUnits, startedAt), verbatim. */
    general: string[];
    /** Every problem, lines and general together. */
    total: number;
}

/**
 * The server's 400: `errors[]` as "deliverables[3].months: months [13] on
 * 'G1' fall outside 1..12" or "pageUnits: ...". Index i is the line sent at
 * index i. A refusal with no field entries (a bare code such as
 * "invalid_engagement") is said by its `message`.
 */
export function parseRefusal(errors: unknown, message: unknown, sentRids: string[]): Refusal {
    const list = Array.isArray(errors) ? errors.filter((e): e is string => typeof e === "string" && e.trim() !== "") : [];
    const byRid: Record<string, string[]> = {};
    const general: string[] = [];
    let fielded = 0;
    for (const entry of list) {
        const m = entry.match(/^deliverables\[(\d+)\]\.[A-Za-z_]+:\s*([\s\S]*)$/);
        const rid = m ? sentRids[Number(m[1])] : undefined;
        if (m && rid) {
            (byRid[rid] ??= []).push(m[2]);
            fielded += 1;
        } else if (/^[A-Za-z_][\w.[\]]*:\s/.test(entry)) {
            general.push(entry);
            fielded += 1;
        }
    }
    if (fielded === 0) {
        const said = typeof message === "string" && message.trim() ? message.trim() : list[0] ?? "The server refused the change.";
        general.push(said);
    }
    return { byRid, general, total: Object.values(byRid).reduce((n, l) => n + l.length, 0) + general.length };
}

/** The server's warnings after a save, each said with the line it is about when it names one. */
export function warningLines(warnings: unknown, labelAt: (i: number) => string | null): string[] {
    if (!Array.isArray(warnings)) return [];
    return warnings
        .map((w) => {
            if (typeof w === "string") return w;
            const o = w as { field?: unknown; message?: unknown };
            const msg = typeof o?.message === "string" ? o.message : "";
            if (!msg) return "";
            const i = typeof o.field === "string" ? o.field.match(/^deliverables\[(\d+)\]/)?.[1] : undefined;
            const label = i !== undefined ? labelAt(Number(i)) : null;
            return label ? `${label}: ${msg}` : msg;
        })
        .filter(Boolean);
}

/* ------------------------------------------------------------------------- *
 * Priority keywords (#3975)
 * ------------------------------------------------------------------------- */

/**
 * The catalogue captures 5 to 10 priority keywords at the quotation
 * discussion, and the schedule cannot be published while the contract holds
 * fewer: `priority_keywords_incomplete` blocks it, so the merchant never sees
 * a date (ourteam-backend#5256). The bounds are the server's, repeated here
 * only so the field can say what is wrong before a round trip.
 */
export const KEYWORDS_MIN = 5;
export const KEYWORDS_MAX = 10;

/** The field's DOM id, so a publish conflict can put the cursor in it. */
export const KEYWORDS_FIELD_ID = "oura-plan-priority-keywords";

/**
 * The keywords as they would be sent: trimmed, blanks dropped, duplicates
 * dropped keeping the first spelling (case-insensitively, so "Facial" and
 * "facial" are one phrase, as the server treats them). Inner spacing is
 * collapsed; nothing else about a phrase is changed.
 */
export function cleanKeywords(list: readonly string[]): string[] {
    const out: string[] = [];
    const seen = new Set<string>();
    for (const raw of list) {
        const phrase = raw.replace(/\s+/g, " ").trim();
        if (!phrase) continue;
        const key = phrase.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(phrase);
    }
    return out;
}

/** The keywords on a contract, cleaned. [] when the server recorded none. */
export function keywordsOf(e: Pick<Engagement, "tracking">): string[] {
    const list = e.tracking?.priority_keywords;
    return Array.isArray(list) ? cleanKeywords(list.filter((k): k is string => typeof k === "string")) : [];
}

/**
 * What is wrong with the keywords as they stand, in the words the field says
 * it, or null when they are fine. [] is NOT an error on its own: a contract
 * that never recorded any is not a draft someone broke, so the field says
 * what is needed and the publish card is what refuses.
 */
export function keywordsProblem(list: readonly string[]): string | null {
    const clean = cleanKeywords(list);
    if (clean.length === 0) return null;
    if (clean.length < KEYWORDS_MIN) return `${KEYWORDS_MIN} to ${KEYWORDS_MAX} keywords. ${clean.length} so far. Add ${KEYWORDS_MIN - clean.length} more.`;
    if (clean.length > KEYWORDS_MAX) return `${KEYWORDS_MIN} to ${KEYWORDS_MAX} keywords. ${clean.length} so far. Take ${clean.length - KEYWORDS_MAX} out.`;
    return null;
}

/** Whether these keywords are enough for the schedule to be published. */
export function keywordsComplete(list: readonly string[]): boolean {
    const n = cleanKeywords(list).length;
    return n >= KEYWORDS_MIN && n <= KEYWORDS_MAX;
}

/**
 * `tracking` to send with a plan save: the contract's own keys, with the
 * keywords replaced. Merged, never replaced — the baseline and
 * `source_assessment_id` live in here and a bare `{priority_keywords}` would
 * drop them.
 */
export function trackingWithKeywords(e: Pick<Engagement, "tracking">, list: readonly string[]): EngagementTracking {
    return { ...(e.tracking ?? {}), priority_keywords: cleanKeywords(list) };
}

/* ------------------------------------------------------------------------- *
 * The signature questions (ourteam-backend#5333)
 * ------------------------------------------------------------------------- */

/**
 * The schedule cannot be published until the contract has two signature
 * questions: one the business is already named for (`visibility`) and one no
 * rival is named for (`coverage`). The server picks each from the AI check
 * when the check supports it. A business no AI answer names yet has no
 * question it already wins, so the check can never fill `visibility`, and
 * until these fields existed nothing on any screen could: `signature_incomplete`
 * stayed on for good and the merchant was never told a date.
 */
export interface SignatureDraft {
    visibility: string;
    coverage: string;
}

/** The first field's DOM id, so a publish conflict can put the cursor in it. */
export const SIGNATURE_FIELD_ID = "oura-plan-signature-visibility";

/** A question, not an essay: the field stops here rather than sending more. */
export const SIGNATURE_MAX_LENGTH = 200;

/** Both parts as they would be sent: inner spacing collapsed, ends trimmed. */
export function cleanSignature(s: SignatureDraft): SignatureDraft {
    const tidy = (v: string) => v.replace(/\s+/g, " ").trim();
    return { visibility: tidy(s.visibility), coverage: tidy(s.coverage) };
}

/** What staff recorded on the contract. "" for a part nobody recorded. */
export function signatureOf(e: Pick<Engagement, "tracking">): SignatureDraft {
    const kept = e.tracking?.signature;
    const part = (v: unknown) => (typeof v === "string" ? v : "");
    return cleanSignature({ visibility: part(kept?.visibility), coverage: part(kept?.coverage) });
}

/** Whether two signatures are the same once cleaned. */
export function sameSignature(a: SignatureDraft, b: SignatureDraft): boolean {
    const x = cleanSignature(a);
    const y = cleanSignature(b);
    return x.visibility === y.visibility && x.coverage === y.coverage;
}

/**
 * `tracking` with the signature replaced. Both parts are always sent: the
 * server removes a part sent blank, which is how staff hand a question back to
 * the AI check's own pick.
 */
export function trackingWithSignature(tracking: EngagementTracking, s: SignatureDraft): EngagementTracking {
    return { ...tracking, signature: cleanSignature(s) };
}

/* ------------------------------------------------------------------------- *
 * The outlets' names (#3985)
 * ------------------------------------------------------------------------- */

/**
 * A contract with 2 or more outlets cannot have a per-outlet task ticked until
 * its outlets are NAMED: the server refuses the tick `outlets_not_confirmed`,
 * and before this screen nothing in the app offered the step, so five of one
 * client's seventeen month-1 items were simply stuck.
 *
 * The rules below are the server's own (`check_outlet_names`,
 * `outlets_for`), repeated here only so the field can say what is wrong
 * before a round trip.
 */
export const OUTLET_NAME_MAX = 80;

/** The field's DOM id, so a refused tick can send staff straight to it. */
export const OUTLET_NAMES_FIELD_ID = "oura-plan-outlet-names";

/** The server's refusal code for a per-outlet tick on unnamed outlets. */
export const OUTLETS_NOT_CONFIRMED = "outlets_not_confirmed";

/** One outlet is never asked about: there is nothing to tell apart (`outlets_for`). */
export function outletNamesAsked(e: Pick<Engagement, "outlets">): boolean {
    return (e.outlets ?? 0) >= 2;
}

/**
 * Whether the Outlet names field is shown: for one outlet too (#4592). Its
 * ticks are never gated on the name, but a GBP Pack is named after its
 * outlet, and the server refuses one for an outlet with no name
 * (ourteam-backend#6395), so staff need a place to enter it.
 */
export function outletNamesShown(e: Pick<Engagement, "outlets">): boolean {
    return (e.outlets ?? 0) >= 1;
}

/**
 * Whether the names are confirmed, by the server's rule: exactly as many
 * stored names as the contract sells. A confirmed-at stamp alone is not the
 * test — the count is, because that is what `outlets_for` reads.
 */
export function outletNamesConfirmed(e: Pick<Engagement, "outlets" | "outletNames">): boolean {
    if (!outletNamesAsked(e)) return true;
    const stored = (e.outletNames ?? []).filter((o) => o?.name?.trim());
    return stored.length === e.outlets;
}

/**
 * The rows to edit: one per outlet the contract sells, in contract order.
 *
 * A stored name wins, then a suggestion from the scan for that position, then
 * empty. Suggestions are OFFERED, never counted as an answer — a scan that
 * found 2 branches for a 5-outlet contract must not look like 5 confirmed
 * names, which is exactly the state the server calls unconfirmed.
 */
export function outletNameRows(e: Pick<Engagement, "outlets" | "outletNames" | "outletSuggestions">): string[] {
    const count = Math.max(0, e.outlets ?? 0);
    const stored = e.outletNames ?? [];
    const hints = e.outletSuggestions ?? [];
    return Array.from({ length: count }, (_, i) => (stored[i]?.name ?? hints[i] ?? "").trim());
}

/**
 * What is wrong with the names as they stand, in the server's own words, or
 * null when they would be accepted. Mirrors `check_outlet_names`: the count
 * first, then each name.
 */
export function outletNamesProblem(names: readonly string[], count: number): string | null {
    const trimmed = names.map((n) => (n ?? "").trim());
    if (trimmed.length !== count || trimmed.some((n) => !n)) {
        if (count === 1) return "Give the outlet its name.";
        const given = trimmed.filter(Boolean).length;
        return `The contract sells ${count} ${count === 1 ? "outlet" : "outlets"}. Give ${count} names — ${given} so far.`;
    }
    const seen = new Set<string>();
    for (const name of trimmed) {
        if (name.length > OUTLET_NAME_MAX) return `Each outlet needs its own name, up to ${OUTLET_NAME_MAX} characters.`;
        const key = name.toLowerCase();
        if (seen.has(key)) return `Two outlets cannot share a name. "${name}" is used twice.`;
        seen.add(key);
    }
    return null;
}

/** The most outlets a plan can cover, as the server allows (`OUTLETS_MAX`). */
export const OUTLETS_MAX = 50;

/**
 * The rows for a new outlet count (ourteam-backend#6407): every row keeps its
 * position, so the outlet in row 1 keeps what was ticked for it; new rows take
 * the suggestion for their position when it is not already used.
 */
export function resizeOutletNames(names: readonly string[], count: number, suggestions: readonly string[]): string[] {
    const used = new Set(names.slice(0, count).map((n) => n.trim().toLowerCase()).filter(Boolean));
    return Array.from({ length: count }, (_, i) => {
        if (i < names.length) return names[i];
        const hint = (suggestions[i] ?? "").trim();
        if (!hint || used.has(hint.toLowerCase())) return "";
        used.add(hint.toLowerCase());
        return hint;
    });
}

/**
 * The client's own Setup answer, put into the rows (ourteam-backend#6407): the
 * count becomes its length (never fewer than the rows already named), a named
 * row keeps its name and its position, and the empty rows take the answer's
 * names not used yet, in order.
 */
export function namesFromAnswer(names: readonly string[], filed: readonly string[]): string[] {
    const answer = filed.map((n) => n.trim()).filter(Boolean);
    const lastNamed = names.reduce((at, n, i) => (n.trim() ? i + 1 : at), 0);
    const count = Math.min(OUTLETS_MAX, Math.max(answer.length, lastNamed, 1));
    const used = new Set(names.map((n) => n.trim().toLowerCase()).filter(Boolean));
    const left = answer.filter((n) => !used.has(n.toLowerCase()));
    return Array.from({ length: count }, (_, i) => {
        const kept = (names[i] ?? "").trim();
        return kept ? names[i] : left.shift() ?? "";
    });
}

/** The names as the PATCH sends them: trimmed, in contract order, plain strings. */
export function outletNamesToSend(names: readonly string[]): string[] {
    return names.map((n) => (n ?? "").trim());
}

/** The server's bounds for an outlet's quote address and phone (ourteam-backend#5808). */
export const OUTLET_ADDRESS_MAX = 300;
export const OUTLET_PHONE_MAX = 60;

/** An outlet's address and phone as the signed quote gives them, as edited. "" is not recorded. */
export interface OutletDetail {
    address: string;
    phone: string;
}

/** The stored quote details, one per outlet row. Absent or null reads as "" (#4776). */
export function outletDetailRows(e: Pick<Engagement, "outlets" | "outletNames">): OutletDetail[] {
    const count = Math.max(0, e.outlets ?? 0);
    const stored = e.outletNames ?? [];
    return Array.from({ length: count }, (_, i) => ({ address: (stored[i]?.address ?? "").trim(), phone: (stored[i]?.phone ?? "").trim() }));
}

/** The details for a new count: kept rows keep theirs, new rows start empty. */
export function resizeOutletDetails(details: readonly OutletDetail[], count: number): OutletDetail[] {
    return Array.from({ length: count }, (_, i) => details[i] ?? { address: "", phone: "" });
}

/** Whether any outlet's quote address or phone differs from what is stored. */
export function outletDetailsChanged(details: readonly OutletDetail[], saved: readonly OutletDetail[]): boolean {
    return details.some((d, i) => d.address.trim() !== (saved[i]?.address ?? "") || d.phone.trim() !== (saved[i]?.phone ?? ""));
}

/**
 * The names to send, each with only the quote details staff changed: an
 * untouched row stays a plain name, so the server keeps what it stored, and a
 * cleared field goes as null, which clears it (ourteam-backend#5808).
 */
export function outletEntriesToSend(
    names: readonly string[],
    details: readonly OutletDetail[],
    saved: readonly OutletDetail[],
): (string | OutletNameSent)[] {
    return outletNamesToSend(names).map((name, i) => {
        const d = details[i];
        if (!d) return name;
        const out: OutletNameSent = { name };
        const address = d.address.trim();
        const phone = d.phone.trim();
        if (address !== (saved[i]?.address ?? "")) out.address = address || null;
        if (phone !== (saved[i]?.phone ?? "")) out.phone = phone || null;
        return "address" in out || "phone" in out ? out : name;
    });
}

/**
 * The places Plan can be opened AT, each by the DOM id of the thing to look
 * at. One list, so a screen that sends staff somewhere and the screen that
 * receives them cannot disagree about where that is.
 */
export type PlanSection = "key-pages" | "outlet-names";
export const PLAN_SECTION_ID: Record<PlanSection, string> = {
    "key-pages": "key-pages",
    "outlet-names": OUTLET_NAMES_FIELD_ID,
};
export function isPlanSection(value: string): value is PlanSection {
    return value === "key-pages" || value === "outlet-names";
}
